// core/lib/realtime.ts — websocket manager for the single `/ws/app/` endpoint.
//
// Module-level state, not a class — matches this codebase's other core/lib
// files (see apiClient.ts). Auth is the existing session cookie: a same-origin
// (dev-proxied / prod-Caddy-proxied) WebSocket connection carries it
// automatically, so there's no token to attach here (that's the mobile
// client's job, via a different client entirely).
//
// Envelope both ways: {"type": string, "payload": object}.

import {
  handleNotificationNew,
  handleDmMessage,
  handleDmRead,
  handleTyping,
  handlePresence,
  handleFeedNew,
  handleConversationAccepted,
  handleConversationRemoved,
  type DmMessagePayload,
  type DmReadPayload,
  type TypingPayload,
  type PresencePayload,
  type FeedNewPayload,
  type ConversationAcceptedPayload,
  type ConversationRemovedPayload,
} from './realtimeHandlers';
import type { Notification } from '@/core/models/notification';

interface InboundEnvelope {
  type: string;
  payload: unknown;
}

let socket: WebSocket | null = null;
// True only when disconnect() closed the socket itself — distinguishes an
// intentional close (no reconnect) from a server/network drop (reconnect).
let intentionallyClosed = true;
let reconnectAttempt = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let connected = false;

// Client -> server `typing` throttle: at most one send per conversation-agnostic
// 3s window (module-level timestamp, matching the non-reactive style the rest
// of this file uses for connection bookkeeping).
let lastTypingSentAt = 0;
const TYPING_THROTTLE_MS = 3000;

const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30_000;

function buildUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws/app/`;
}

/** Dispatch a parsed inbound message by `type` to the matching store-effect handler. */
export function handleMessage(raw: string): void {
  let envelope: InboundEnvelope;
  try {
    envelope = JSON.parse(raw) as InboundEnvelope;
  } catch {
    // Malformed frame — nothing sane to do with it, drop silently rather than
    // throwing out of a WebSocket event handler.
    return;
  }
  switch (envelope.type) {
    case 'notification.new':
      handleNotificationNew(envelope.payload as Notification);
      break;
    case 'dm.message':
      handleDmMessage(envelope.payload as DmMessagePayload);
      break;
    case 'dm.read':
      handleDmRead(envelope.payload as DmReadPayload);
      break;
    case 'typing':
      handleTyping(envelope.payload as TypingPayload);
      break;
    case 'presence':
      handlePresence(envelope.payload as PresencePayload);
      break;
    case 'feed.new':
      handleFeedNew(envelope.payload as FeedNewPayload);
      break;
    case 'conversation.accepted':
      handleConversationAccepted(envelope.payload as ConversationAcceptedPayload);
      break;
    case 'conversation.removed':
      handleConversationRemoved(envelope.payload as ConversationRemovedPayload);
      break;
    default:
      // Unknown event type — forward compatible: ignore rather than throw.
      break;
  }
}

function scheduleReconnect(): void {
  if (reconnectTimer !== null) return; // already scheduled
  const exponential = Math.min(
    MAX_RECONNECT_DELAY_MS,
    BASE_RECONNECT_DELAY_MS * 2 ** reconnectAttempt,
  );
  // Jitter: 50%-100% of the computed delay, so a fleet of clients dropped by
  // the same server blip doesn't all reconnect in the same instant.
  const delay = exponential * (0.5 + Math.random() * 0.5);
  reconnectAttempt += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

/**
 * Open the socket. Idempotent — a no-op while a socket is already open or
 * connecting, so callers (e.g. an effect that re-runs for unrelated reasons)
 * can call this freely without risking a duplicate connection.
 */
export function connect(): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  intentionallyClosed = false;
  let ws: WebSocket;
  try {
    // W11 (1.21.00 review, parity with mobile's realtime.ts, which already
    // wraps its own socket construction): the WebSocket constructor can
    // throw synchronously (e.g. an invalid/blocked URL) rather than only
    // failing asynchronously via onerror/onclose — an uncaught throw here
    // would propagate out of connect() into whatever effect called it.
    ws = new WebSocket(buildUrl());
  } catch {
    connected = false;
    scheduleReconnect();
    return;
  }
  socket = ws;

  ws.onopen = () => {
    connected = true;
    // A socket that opens after at least one failed attempt is a RECONNECT: whatever
    // the server pushed while it was down never arrived, so listeners refetch.
    const reconnected = reconnectAttempt > 0 || hasConnectedBefore;
    hasConnectedBefore = true;
    reconnectAttempt = 0;
    if (reconnected) reconnectListeners.forEach((listener) => listener());
  };

  ws.onmessage = (event: MessageEvent) => {
    if (typeof event.data === 'string') handleMessage(event.data);
  };

  ws.onclose = () => {
    connected = false;
    socket = null;
    if (!intentionallyClosed) scheduleReconnect();
  };

  // The browser fires a close event after an error on a WebSocket, so onclose
  // above is where reconnect scheduling lives; onerror just exists so an
  // unhandled-error console spam doesn't distract from a routine drop.
  ws.onerror = () => {};
}

let hasConnectedBefore = false;
const reconnectListeners = new Set<() => void>();

/**
 * Run `listener` every time the socket comes back after a drop (not on the first
 * connection of a session). Returns the unsubscribe function.
 */
export function onReconnect(listener: () => void): () => void {
  reconnectListeners.add(listener);
  return () => {
    reconnectListeners.delete(listener);
  };
}

/** Close the socket cleanly and reset state. Never triggers a reconnect. Safe when not connected. */
export function disconnect(): void {
  intentionallyClosed = true;
  hasConnectedBefore = false;
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectAttempt = 0;
  connected = false;
  if (socket) {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    socket.close();
    socket = null;
  }
}

/** Current connection state, for callers (e.g. the polling-gate hook) that need a plain, non-reactive check. */
export function isConnected(): boolean {
  return connected;
}

/** Send a `typing` event for `conversationId`, throttled to at most one per 3s. No-ops if the socket isn't open. */
export function sendTyping(conversationId: string): void {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  const now = Date.now();
  if (now - lastTypingSentAt < TYPING_THROTTLE_MS) return;
  lastTypingSentAt = now;
  socket.send(JSON.stringify({ type: 'typing', payload: { conversation: conversationId } }));
}
